import { FitLine, FitZone } from './fitText';

/**
 * The outlined pad — the press block with no solid fills in it: every piece of
 * the pad's furniture is drawn as a rule. The heading sits in a box whose top
 * edge the plate breaks, the address runs in an outlined bar, নাম / ঠিকানা are
 * boxes with the word ruled off inside them, and the grid's head is outlined
 * rather than filled, so the whole sheet prints in one pass of thin ink.
 *
 * Measured in A4 pixels and multiplied by `u` (the paper's width against A4),
 * so an A6 book is the same pad printed small.
 */

const INK = '#1C1815';

/** The heading box's height, as a share of the paper's width. */
const HEAD_RATIO = 0.3;

/** The words this pad prints that the other styles word differently. */
const OUTLINE_LABELS = {
    bn: { memoNo: 'ক্রমিক নং', inWords: 'কথায়', phone: 'মোবাইল', email: 'ইমেইল', colon: 'ঃ' },
    en: { memoNo: 'SL No.', inWords: 'In words', phone: 'Mobile', email: 'Email', colon: ':' },
};

/** A word ruled off inside the box it labels — নাম, ঠিকানা. */
function RuledField({ label, u, accent }) {
    return (
        <div
            style={{
                display: 'flex', alignItems: 'stretch', height: 36 * u,
                border: `${1.4 * u}px solid ${accent}`, borderRadius: 4 * u, overflow: 'hidden',
            }}
        >
            <div
                style={{
                    flex: 'none', minWidth: 92 * u, borderRight: `${1.4 * u}px solid ${accent}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: `0 ${10 * u}px`, fontSize: 19 * u, fontWeight: 700, lineHeight: 1.2,
                    whiteSpace: 'nowrap', color: accent,
                }}
            >
                <span>{label}</span>
            </div>
            <div style={{ flex: 1 }} />
        </div>
    );
}

/** The ruled writing area: an outlined head over an open grid. */
function OutlineGrid({ memo, u, accent }) {
    const totalFlex = memo.columns.reduce((sum, col) => sum + (col.flex || 1), 0);
    const rule = `${1.2 * u}px solid ${accent}`;
    const last = memo.columns.length - 1;

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                {memo.columns.map((col) => (
                    <col key={col.id} style={{ width: `${((col.flex || 1) / totalFlex) * 100}%` }} />
                ))}
            </colgroup>
            <thead>
                <tr>
                    {memo.columns.map((col, i) => (
                        <th
                            key={col.id}
                            style={{
                                color: accent,
                                height: 36 * u,
                                padding: `0 ${3 * u}px`,
                                fontSize: 20 * u,
                                fontWeight: 700,
                                lineHeight: 1.3,
                                textAlign: 'center',
                                borderTop: `${1.6 * u}px solid ${accent}`,
                                borderBottom: rule,
                                borderLeft: (memo.columnLines || i === 0) ? rule : 'none',
                                borderRight: (memo.columnLines || i === last) ? rule : 'none',
                            }}
                        >
                            <span>{col.label || ' '}</span>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: memo.rows }, (_, i) => (
                    <tr key={i}>
                        {memo.columns.map((col, c) => (
                            <td
                                key={col.id}
                                style={{
                                    height: 30 * u,
                                    padding: 0,
                                    borderLeft: (memo.columnLines || c === 0) ? rule : 'none',
                                    borderRight: (memo.columnLines || c === last) ? rule : 'none',
                                    borderBottom: memo.rowLines && i < memo.rows - 1 ? rule : 'none',
                                }}
                            />
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/** মোট / অগ্রিম / বাকী, ruled into the last two columns of the same grid. */
function OutlineTotals({ memo, labels, u, accent }) {
    const rows = [
        memo.totals.total ? labels.total : null,
        memo.totals.advance ? labels.advance : null,
        memo.totals.due ? labels.due : null,
    ].filter(Boolean);

    const totalFlex = memo.columns.reduce((sum, col) => sum + (col.flex || 1), 0);
    const rule = `${1.2 * u}px solid ${accent}`;
    const span = Math.max(0, memo.columns.length - 2);

    if (!rows.length) {
        return <div style={{ borderTop: rule, borderLeft: rule, borderRight: rule, height: 0 }} />;
    }

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
                {memo.columns.map((col) => (
                    <col key={col.id} style={{ width: `${((col.flex || 1) / totalFlex) * 100}%` }} />
                ))}
            </colgroup>
            <tbody>
                {rows.map((label, i) => {
                    const lastRow = i === rows.length - 1;
                    return (
                        <tr key={label}>
                            {memo.columns.slice(0, span).map((col, c) => (
                                <td
                                    key={col.id}
                                    style={{
                                        borderLeft: (memo.columnLines || c === 0) ? rule : 'none',
                                        borderRight: memo.columnLines && c === span - 1 ? rule : 'none',
                                        borderBottom: lastRow ? rule : 'none',
                                        height: 32 * u,
                                        padding: 0,
                                    }}
                                />
                            ))}
                            <td
                                style={{
                                    border: rule,
                                    borderBottom: lastRow ? rule : 'none',
                                    height: 32 * u,
                                    padding: `0 ${8 * u}px`,
                                    fontSize: 19 * u,
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    textAlign: 'right',
                                    color: accent,
                                }}
                            >
                                <span>{label}</span>
                            </td>
                            <td style={{ border: rule, borderBottom: lastRow ? rule : 'none', height: 32 * u, padding: 0 }} />
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

/* ------------------------------------------------------------------ *
 *  The sheet
 * ------------------------------------------------------------------ */

export default function OutlineMemo({ memo, labels, title, accent, paperWidth, u }) {
    const { shop, show } = memo;
    const lang = memo.docLang === 'en' ? 'en' : 'bn';
    const words = OUTLINE_LABELS[lang];
    const on = (key) => show[key] !== false;
    const pad = 34 * u;
    const headHeight = Math.round(paperWidth * HEAD_RATIO);
    const headPad = { top: 20 * u, side: 26 * u, bottom: 14 * u };
    const phones = [shop.phone, shop.phone2].filter((n) => String(n || '').trim());
    const email = String(shop.email || '').trim();
    const addressLine = shop.address.split('\n').map((l) => l.trim()).filter(Boolean).join(', ');
    const chips = [
        on('phone') && phones.length ? `${words.phone}${words.colon} ${phones.join(', ')}` : '',
        on('email') && email ? `${words.email}${words.colon} ${email}` : '',
    ].filter(Boolean);
    const headKey = [
        memo.bismillah, title, shop.name, memo.proprietor, memo.distributor, shop.tagline,
        chips.join('|'), shop.logo ? '1' : '0', JSON.stringify(show), u,
    ].join('~');

    return (
        <div
            style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                padding: `${pad}px ${pad}px ${26 * u}px`, boxSizing: 'border-box', color: INK,
            }}
        >
            {/* ---- the heading, in a box the plate breaks ---- */}
            <div style={{ position: 'relative', flex: 'none', marginBottom: 12 * u }}>
                <div
                    style={{
                        position: 'relative',
                        border: `${2 * u}px solid ${accent}`,
                        borderRadius: 10 * u,
                    }}
                >
                    {/* The plate sits astride the top rule, as the block is cut. */}
                    {on('plate') ? (
                        <div
                            style={{
                                position: 'absolute', left: '50%', top: 0, zIndex: 1,
                                transform: 'translate(-50%, -50%)',
                                background: '#FFFFFF', color: accent,
                                border: `${1.6 * u}px solid ${accent}`, borderRadius: 999 * u,
                                padding: `${2 * u}px ${20 * u}px`,
                                fontSize: 18 * u, fontWeight: 800, lineHeight: 1.35, whiteSpace: 'nowrap',
                            }}
                        >
                            <span>{title}</span>
                        </div>
                    ) : null}

                    {on('logo') && shop.logo ? (
                        <div
                            aria-hidden="true"
                            style={{
                                position: 'absolute', left: 16 * u, top: 16 * u, zIndex: 1,
                                width: 96 * u, height: 52 * u,
                                backgroundImage: `url("${shop.logo}")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'left center',
                                backgroundSize: 'contain',
                            }}
                        />
                    ) : null}

                    <FitZone height={headHeight} align="centre" pad={headPad} signature={headKey}>
                        {/* Clear of the logo in the corner, so a long name is cut
                            to the room it really has rather than running under it. */}
                        <div style={{ textAlign: 'center', paddingLeft: on('logo') && shop.logo ? 92 * u : 0 }}>
                            {on('bismillah') && memo.bismillah.trim() ? (
                                <div style={{ fontSize: 14 * u, fontWeight: 500, lineHeight: 1.3, color: '#5A5248' }}>
                                    {memo.bismillah}
                                </div>
                            ) : null}

                            <FitLine
                                text={shop.name || ' '}
                                size={52 * u}
                                style={{ marginTop: 3 * u, fontWeight: 800, lineHeight: 1.12, color: accent }}
                            />

                            {on('distributor') && memo.distributor.trim() ? (
                                <FitLine
                                    text={memo.distributor}
                                    size={18 * u}
                                    style={{ marginTop: 2 * u, fontWeight: 700, lineHeight: 1.3, color: INK }}
                                />
                            ) : null}

                            {on('tagline') && shop.tagline.trim() ? (
                                <div
                                    style={{
                                        fontSize: 14 * u, fontWeight: 500, lineHeight: 1.4, color: '#5A5248',
                                        margin: `${2 * u}px auto 0`, maxWidth: '92%',
                                    }}
                                >
                                    {shop.tagline}
                                </div>
                            ) : null}

                            {/* The proprietor and the numbers, each ruled off in a
                                plate of its own rather than filled. */}
                            {(on('proprietor') && memo.proprietor.trim()) || chips.length ? (
                                <div
                                    style={{
                                        display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
                                        gap: `${5 * u}px ${8 * u}px`, marginTop: 7 * u,
                                    }}
                                >
                                    {on('proprietor') && memo.proprietor.trim() ? (
                                        <div
                                            style={{
                                                border: `${1.4 * u}px solid ${accent}`, borderRadius: 999 * u,
                                                padding: `${2 * u}px ${16 * u}px`,
                                                fontSize: 18 * u, fontWeight: 700, lineHeight: 1.35,
                                                color: accent, whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <span>{memo.proprietor}</span>
                                        </div>
                                    ) : null}
                                    {chips.map((chip) => (
                                        <div
                                            key={chip}
                                            style={{
                                                border: `${1.2 * u}px solid ${accent}`, borderRadius: 999 * u,
                                                padding: `${2 * u}px ${14 * u}px`,
                                                fontSize: 16 * u, fontWeight: 600, lineHeight: 1.35,
                                                color: INK, whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <span>{chip}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </FitZone>
                </div>
            </div>

            {/* ---- the address, ruled across the sheet ---- */}
            {on('addressBand') && addressLine ? (
                <div
                    style={{
                        flex: 'none', border: `${1.4 * u}px solid ${accent}`, borderRadius: 4 * u,
                        padding: `${4 * u}px ${16 * u}px`, textAlign: 'center', marginBottom: 12 * u,
                    }}
                >
                    <FitLine
                        text={`${labels.address}${words.colon} ${addressLine}`}
                        size={20 * u}
                        style={{ fontWeight: 700, lineHeight: 1.35, color: INK }}
                    />
                </div>
            ) : null}

            {/* ---- the form ---- */}
            {(memo.fields.memoNo || memo.fields.date) ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 * u, marginBottom: 10 * u }}>
                    <span style={{ fontSize: 19 * u, fontWeight: 700, lineHeight: 1.4 }}>
                        {memo.fields.memoNo ? `${words.memoNo}${words.colon}` : ''}
                    </span>
                    {memo.fields.date ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 * u }}>
                            <span style={{ fontSize: 19 * u, fontWeight: 700, lineHeight: 1.4 }}>
                                {`${labels.date}${words.colon}`}
                            </span>
                            <span
                                style={{
                                    display: 'flex', alignItems: 'stretch', height: 30 * u,
                                    border: `${1.4 * u}px solid ${accent}`, borderRadius: 4 * u, overflow: 'hidden',
                                }}
                            >
                                {[44, 44, 72].map((width, i) => (
                                    <span
                                        key={i}
                                        style={{
                                            display: 'block', width: width * u,
                                            borderLeft: i === 0 ? 'none' : `${1.2 * u}px solid ${accent}`,
                                        }}
                                    />
                                ))}
                            </span>
                        </span>
                    ) : null}
                </div>
            ) : null}

            {memo.fields.name ? <RuledField label={labels.name} u={u} accent={accent} /> : null}
            {memo.fields.address ? (
                <div style={{ marginTop: 7 * u }}>
                    <RuledField label={labels.address} u={u} accent={accent} />
                </div>
            ) : null}

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', marginTop: 10 * u }}>
                <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch' }}>
                    <OutlineGrid memo={memo} u={u} accent={accent} />
                </div>
                <OutlineTotals memo={memo} labels={labels} u={u} accent={accent} />

                {on('slogan') && memo.slogan.trim() ? (
                    <div
                        aria-hidden="true"
                        style={{
                            position: 'absolute', left: '10%', right: '30%', bottom: '10%',
                            textAlign: 'center', pointerEvents: 'none',
                            fontSize: 28 * u, fontWeight: 600, lineHeight: 1.2,
                            color: accent, opacity: 0.14, whiteSpace: 'nowrap',
                            transform: 'skewX(-10deg)',
                        }}
                    >
                        {memo.slogan}
                    </div>
                ) : null}
            </div>

            {memo.showWords ? (
                <div
                    style={{
                        display: 'flex', alignItems: 'stretch', height: 32 * u, marginTop: 12 * u,
                        border: `${1.4 * u}px solid ${accent}`, borderRadius: 4 * u, overflow: 'hidden',
                    }}
                >
                    <span
                        style={{
                            flex: 'none', display: 'flex', alignItems: 'center',
                            borderRight: `${1.4 * u}px solid ${accent}`, padding: `0 ${12 * u}px`,
                            fontSize: 19 * u, fontWeight: 700, lineHeight: 1.2, color: accent, whiteSpace: 'nowrap',
                        }}
                    >
                        <span>{`${words.inWords}${words.colon}`}</span>
                    </span>
                    <span style={{ flex: 1 }} />
                </div>
            ) : null}

            {memo.footerNote ? (
                <div style={{ marginTop: 8 * u, textAlign: 'center', fontSize: 15 * u, fontWeight: 500, lineHeight: 1.4, color: '#5A5248' }}>
                    {memo.footerNote}
                </div>
            ) : null}

            <div style={{ flex: 'none', minHeight: 36 * u }} />

            {memo.showSignature ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 * u }}>
                    {[labels.customerSign, labels.sellerSign].map((caption) => (
                        <div key={caption} style={{ width: 210 * u, textAlign: 'center' }}>
                            <div style={{ borderTop: `${1.4 * u}px solid ${accent}`, paddingTop: 4 * u, fontSize: 17 * u, fontWeight: 600, lineHeight: 1.35, color: INK }}>
                                {caption}
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
